import { Grid, Skeleton } from "@mui/material";
import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MultiAxisLineChart from "shared/components/lineGraph";
import RangeDatePicker from "shared/components/rangeDatePicker";
import { routeConstant } from "shared/routes/routesConstant";
import { getDashboardStatsProjectHeadNew } from "shared/services/projectHeadService";
import { user_roles } from "shared/utils/constants";
import styles from "./style.module.scss";

let headingStats = [
  {
    heading: "Individual Accounts",
    total_users: 0,
    id: 1,
    keyword: "individuals",
    user_id: user_roles.reader,
  },
  {
    heading: "Total Parent Accounts",
    total_users: 0,
    id: 2,
    keyword: "parents",
    user_id: user_roles.parent,
  },
  {
    heading: "Total School Accounts",
    total_users: 0,
    id: 3,
    keyword: "schools",
    user_id: user_roles.school,
  },
];

let accountStats = [
  {
    heading: "New Sign-up Account:",
    current: 0,
    previous: 0,
    precentage: 0,
    color: "#FFECFF",
    statsIndex: 100,
  },
  {
    heading: "Free Account:",
    current: 0,
    previous: 0,
    precentage: 0,
    color: "#FFFBF3",
    statsIndex: 100,
  },
  {
    heading: "Premium Account:",
    current: 0,
    previous: 0,
    precentage: 0,
    color: "#E6F6FD",
    statsIndex: -100,
  },
];

function AnalyticalGrid() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(1);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [activeRole, setActiveRole] = useState<number>(user_roles.reader);
  const [headers, setHeaders] = useState<any>(headingStats);
  const [activeStats, setActiveStats] = useState<any>(accountStats);
  const [graphData, setGraphData] = useState<any>([]);
  const [loader, setLoader] = useState<boolean>(false);

  const getStats = () => {
    setLoader(true);
    getDashboardStatsProjectHeadNew(
      {
        from: moment(startDate).format("YYYY-MM-DD"),
        to: moment(endDate).format("YYYY-MM-DD"),
      },
      activeRole
    )
      .then(
        ({
          data: {
            data: { stats, graph, tabs },
          },
        }) => {
          setGraphData(graph);
          setLoader(false);

          setHeaders([
            {
              heading: "Individual Accounts",
              total_users: tabs?.total_users,
              id: 1,
              keyword: "individuals",
              user_id: user_roles.reader,
            },
            {
              heading: "Total Parent Accounts",
              total_users: tabs?.total_parents,
              id: 2,
              keyword: "parents",
              user_id: user_roles.parent,
            },
            {
              heading: "Total School Accounts",
              total_users: tabs?.total_schools,
              id: 3,
              keyword: "schools",
              user_id: user_roles.school,
            },
          ]);

          setActiveStats([
            {
              heading: "New Sign-up Account:",
              current: stats?.users?.current,
              previous: stats?.users?.previous,
              precentage: stats?.users?.percentage,
              color: "#FFECFF",
              statsIndex: 100,
            },
            {
              heading: "Free Account:",
              current: stats?.free?.current,
              previous: stats?.free?.previous,
              precentage: stats?.free?.percentage,
              color: "#FFFBF3",
              statsIndex: 100,
            },
            {
              heading: "Premium Account:",
              current: stats?.memberships?.current,
              previous: stats?.memberships?.previous,
              precentage: stats?.memberships?.percentage,
              color: "#E6F6FD",
              statsIndex: -100,
            },
          ]);
        }
      )
      .catch((err) => {
        console.log("err", err);
        setLoader(false);
      });
  };

  useEffect(() => {
    if (startDate && endDate) {
      getStats();
    }

    //eslint-disable-next-line
  }, [activeRole, startDate, endDate]);

  let dataSetsForGraphs = [
    {
      label: "Total Accounts",
      data: graphData?.users_joined,
      borderColor: "#9A469B",
      backgroundColor: "#9A469B",
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBorderColor: "rgba(0, 0, 0, 0)",
      pointBackgroundColor: "rgba(0, 0, 0, 0)",
      pointHoverBackgroundColor: "#9A469B",
      pointHoverBorderColor: "#FFFFFF",
    },
    {
      label: "Premium Accounts",
      data: graphData?.premium,
      borderColor: "#EF437B",
      backgroundColor: "#EF437B",
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBorderColor: "rgba(0, 0, 0, 0)",
      pointBackgroundColor: "rgba(0, 0, 0, 0)",
      pointHoverBackgroundColor: "#EF437B",
      pointHoverBorderColor: "#FFFFFF",
    },
    {
      label: "Free Accounts",
      data: graphData?.free,
      borderColor: "#1897A6",
      backgroundColor: "#1897A6",
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBorderColor: "rgba(0, 0, 0, 0)",
      pointBackgroundColor: "rgba(0, 0, 0, 0)",
      pointHoverBackgroundColor: "#1897A6",
      pointHoverBorderColor: "#FFFFFF",
    },
  ];

  return (
    <div className={styles.mainContainer}>
      <Grid className={styles.actionContainer} container>
        {headers?.map((item: any, index: any, arr: any) => {
          return (
            <Grid
              xs={4}
              className={classNames(
                styles.singleHeadingGrid,
                activeTab === item?.id && styles.active,
                index === 0 && styles.borderRadiusLeft,
                index + 1 === arr.length && styles.borderRadiusRight
              )}
              onClick={() => {
                setActiveTab(item?.id);
                setActiveRole(item?.user_id);
              }}
            >
              <div className={styles.headingStats}>{item?.heading}</div>
              {!loader ? (
                <div className={styles.stats}>{item?.total_users}</div>
              ) : (
                <Skeleton variant="rectangular" width={38} height={38} />
              )}
              <div
                className={styles.viewDetail}
                onClick={() => {
                  navigate(
                    routeConstant.usersAccountsDetail?.path?.replace(
                      ":keyword",
                      item?.keyword
                    )
                  );
                }}
              >
                View Details
              </div>
            </Grid>
          );
        })}
      </Grid>

      <div className={styles.dateSelectorContainer}>
        <div className={styles.analyticsHeading}>Account Analytics</div>
        <div>
          <RangeDatePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </div>
      </div>

      <Grid className={styles.accountStatsContainer} container>
        {activeStats?.map((item: any, index: any) => {
          const { color, heading, current, previous, precentage } = item;
          const isPositiveIndex = current >= previous;

          return (
            <Grid
              key={index}
              xs={12}
              md={4}
              sx={{ background: color }}
              className={styles.singleStatsContainer}
            >
              <div className={styles.accountStatsHeading}>{heading}</div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div className={styles.accountStats}>
                  {loader ? (
                    <Skeleton variant="rectangular" width={38} height={38} />
                  ) : (
                    current
                  )}
                </div>
                <div
                  className={classNames(
                    styles.statsIndex,
                    !isPositiveIndex && styles.lowStatsIndex
                  )}
                >
                  {loader ? (
                    <>
                      <Skeleton variant="rectangular" width={60} height={30} />
                    </>
                  ) : (
                    <>
                      {isPositiveIndex ? (
                        <Icons.GreenArrow />
                      ) : (
                        <Icons.RedArrow />
                      )}
                      {Math.abs(precentage).toFixed(2)}%
                    </>
                  )}
                </div>
              </div>
            </Grid>
          );
        })}
      </Grid>

      <div className={styles.graphContainer}>
        {loader ? (
          <Skeleton variant="rectangular" className={styles.graphLoader} />
        ) : (
          <MultiAxisLineChart
            datasets={dataSetsForGraphs}
            legend={false}
            labels={graphData?.labels}
          />
        )}
      </div>
    </div>
  );
}

export default AnalyticalGrid;
